import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { AnalysisProgress, Region } from "../types";

interface RegionAnalysisCardProps {
  region: Region;
  progress: AnalysisProgress;
  thumbnail: string | null;
}

export const RegionAnalysisCard = ({
  region,
  progress,
  thumbnail,
}: RegionAnalysisCardProps) => {
  const { t } = useTranslation();
  const percentage = Math.round(progress.percentage);
  const isComplete = percentage >= 100;

  const getStepState = (stepIndex: number) => {
    if (progress.steps[stepIndex].complete) return "complete";
    if (stepIndex === progress.currentStep) return "active";
    return "pending";
  };

  return (
    <Box
      sx={{
        border: "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
        borderRadius: "var(--card-large, 12px)",
        padding: "var(--spacing20, 20px)",
        width: "280px",
        backgroundColor: "var(--alias-background-white, #fff)",
      }}
    >
      {thumbnail && (
        <Box
          sx={{
            width: "100%",
            height: "100px",
            borderRadius: "var(--card-small, 8px)",
            overflow: "hidden",
            marginBottom: "var(--spacing16, 16px)",
            backgroundColor: "var(--alias-surface-low, #f2f2f2)",
            border: `2px solid ${region.color}`,
          }}
        >
          <img
            src={thumbnail}
            alt={`Region ${region.label}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "var(--spacing8, 8px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing8, 8px)",
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: region.color,
            }}
          />
          <Typography
            sx={{
              fontSize: "var(--body-b2-14-regular-size, 14px)",
              fontWeight: 700,
            }}
          >
            {t("ldp_explore_image_region_label", { label: region.label })}
          </Typography>
        </Box>
        {isComplete ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing4, 4px)",
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 18,
                color: "var(--alias-text-on-white-positive, #4CAF50)",
              }}
            />
            <Typography
              sx={{
                fontSize: "var(--body-b2-14-regular-size, 14px)",
                fontWeight: 700,
                color: "var(--alias-text-on-white-positive, #4CAF50)",
              }}
            >
              {t("ldp_explore_image_analysis_complete")}
            </Typography>
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: "var(--body-b2-14-regular-size, 14px)",
              fontWeight: 700,
              color: "var(--alias-text-on-white-secondary, #6d6d6d)",
            }}
          >
            {percentage}%
          </Typography>
        )}
      </Box>

      {!isComplete && (
        <Box sx={{ marginBottom: "var(--spacing16, 16px)" }}>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: "var(--alias-stroke-on-white-default, #d9d9d9)",
              "& .MuiLinearProgress-bar": {
                backgroundColor: region.color,
                borderRadius: 3,
              },
            }}
          />
        </Box>
      )}

      <List dense disablePadding>
        {progress.steps.map((step, index) => {
          const state = getStepState(index);
          return (
            <ListItem
              key={step.labelKey}
              disablePadding
              sx={{ marginBottom: "var(--spacing4, 4px)" }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {state === "complete" ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: region.color }} />
                ) : (
                  <RadioButtonUncheckedIcon
                    sx={{
                      fontSize: 18,
                      color:
                        state === "active"
                          ? region.color
                          : "var(--alias-text-on-white-disable, #999)",
                    }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  state === "active"
                    ? `${t(step.labelKey)}...`
                    : t(step.labelKey)
                }
                primaryTypographyProps={{
                  sx: {
                    fontSize: "var(--body-b3-12-regular-size, 12px)",
                    color:
                      state === "complete"
                        ? "var(--alias-text-on-white-primary, #1a1a1a)"
                        : state === "active"
                          ? region.color
                          : "var(--alias-text-on-white-disable, #999)",
                  },
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};
